import {
  Block,
  BlockFooter,
  BlockTitle,
  Icon,
  List,
  ListButton,
  ListInput,
  Navbar,
  Page,
  Preloader,
  f7,
} from 'framework7-react';
import { useEffect, useRef, useState } from 'react';
import { CapacityModeCard } from '@/features/reservation-setup/components/CapacityModeCard';
import { AiSurface } from '@/components/AiSurface';
import SettingsSaveNav from '@/components/SettingsSaveNav';
import EmptyState from '@/components/EmptyState';
import { useT } from '@/i18n';
import { ImageUploadError, uploadImage } from '@/lib/image-upload';
import { useRestaurantStore } from '@/stores/restaurant.store';
import type { AllergenTableEntry } from '@restaurant/api-contracts';
import {
  useAllergenSheet,
  useAllergenTable,
  useParseAllergenSheet,
  useSetAllergenSheet,
  useSetAllergenTable,
} from '../hooks/useAllergenLabeling';
import './allergen-labeling.css';

const MAX_EDGE = 2000;

type Kind = 'ALLERGEN' | 'ADDITIVE' | 'DIETARY';
type Row = {
  id: string;
  shortKey: string;
  description: string;
  kind: Kind;
};
type Mode = 'SIMPLE' | 'ADVANCED';

const KIND_ORDER: Kind[] = ['ALLERGEN', 'ADDITIVE', 'DIETARY'];

const toast = (text: string) => f7.toast.show({ text, closeTimeout: 2200, position: 'bottom' });
const uid = () => crypto.randomUUID();

const toRow = (e: { shortKey: string; description: string; kind: Kind }): Row => ({
  id: uid(),
  shortKey: e.shortKey,
  description: e.description,
  kind: e.kind,
});

const cleanRows = (rows: Row[]) =>
  rows
    .map((r) => ({
      shortKey: r.shortKey.trim(),
      description: r.description.trim(),
      kind: r.kind,
    }))
    .filter((r) => r.shortKey);

export default function AllergenLabelingPage() {
  const t = useT();
  const a = t.menu.allergenLabeling;
  const restaurantId = useRestaurantStore((s) => s.selectedRestaurantId);
  const fileInput = useRef<HTMLInputElement>(null);
  const modeInited = useRef(false);

  const sheetQuery = useAllergenSheet();
  const tableQuery = useAllergenTable();
  const setSheet = useSetAllergenSheet();
  const setTable = useSetAllergenTable();
  const parse = useParseAllergenSheet();

  const [mode, setMode] = useState<Mode>('SIMPLE');
  const [text, setText] = useState('');
  const [imageKey, setImageKey] = useState<string | null>(null);
  const [photoUrl, setPhotoUrl] = useState<string | null>(null);
  const [rows, setRows] = useState<Row[]>([]);
  const [imgBusy, setImgBusy] = useState(false);

  useEffect(() => {
    const s = sheetQuery.data;
    if (!s) return;
    setText(s.sheetText ?? '');
    setImageKey(s.sheetImageKey ?? null);
    setPhotoUrl(s.sheetImageUrl ?? null);
    if (!modeInited.current) {
      setMode(s.advanced ? 'ADVANCED' : 'SIMPLE');
      modeInited.current = true;
    }
  }, [sheetQuery.data]);
  useEffect(() => {
    if (!tableQuery.data) return;
    setRows(tableQuery.data.map(toRow));
  }, [tableQuery.data]);

  const baseText = sheetQuery.data?.sheetText ?? null;
  const baseImageKey = sheetQuery.data?.sheetImageKey ?? null;
  const baseTable = tableQuery.data ?? [];

  const effText = text.trim() ? text : null;
  const effImageKey = imageKey;
  const sheetDirty = effText !== baseText || effImageKey !== baseImageKey;
  const tableDirty = JSON.stringify(cleanRows(rows)) !== JSON.stringify(baseTable);
  const hasSheet = Boolean(effText || effImageKey);
  const needsParse = mode === 'SIMPLE' && hasSheet && cleanRows(rows).length === 0;
  const baseAdvanced = sheetQuery.data?.advanced ?? false;
  const advancedDirty = (mode === 'ADVANCED') !== baseAdvanced;
  const isDirty = sheetDirty || tableDirty || needsParse || advancedDirty;
  const saving = setSheet.isPending || setTable.isPending || parse.isPending;

  const modeOptions = [
    {
      value: 'SIMPLE' as const,
      icon: 'doc_text',
      title: a.simpleTitle,
      features: a.simpleFeatures,
      details: a.simpleDetails,
    },
    {
      value: 'ADVANCED' as const,
      icon: 'square_list',
      title: a.advancedTitle,
      features: a.advancedFeatures,
      details: a.advancedDetails,
    },
  ];

  async function parseSheet(): Promise<AllergenTableEntry[] | null> {
    try {
      return await parse.mutateAsync({});
    } catch {
      return null;
    }
  }

  const save = async () => {
    try {
      if (mode === 'ADVANCED') {
        const clean = cleanRows(rows);
        const seen = new Set<string>();
        for (const r of clean) {
          if (seen.has(r.shortKey)) {
            toast(a.duplicateKey(r.shortKey));
            return;
          }
          seen.add(r.shortKey);
        }
        if (advancedDirty) await setSheet.mutateAsync({ advanced: true });
        if (tableDirty) await setTable.mutateAsync(clean);
        toast(a.saved);
        return;
      }
      if (sheetDirty || advancedDirty) {
        await setSheet.mutateAsync({
          ...(sheetDirty ? { sheetText: effText, sheetImageKey: effImageKey } : {}),
          advanced: false,
        });
      }
      if (!hasSheet) {
        setRows([]);
        if (baseTable.length > 0) await setTable.mutateAsync([]);
        toast(a.saved);
        return;
      }
      if (sheetDirty || needsParse) {
        const entries = await parseSheet();
        if (!entries || entries.length === 0) {
          toast(a.parseFailed);
          return;
        }
        setRows(entries.map(toRow));
        await setTable.mutateAsync(entries);
      }
      toast(a.saved);
    } catch (e) {
      toast(e instanceof Error ? e.message : a.saveFailed);
    }
  };

  async function onPickImage(file: File) {
    if (!restaurantId) return;
    setImgBusy(true);
    try {
      const { key } = await uploadImage(file, restaurantId, 'allergen-sheet', {
        maxEdge: MAX_EDGE,
      });
      setImageKey(key);
      setPhotoUrl(URL.createObjectURL(file));
    } catch (err) {
      toast(err instanceof ImageUploadError ? a.imageFailed : a.imageFailed);
    } finally {
      setImgBusy(false);
    }
  }

  const removeImage = () => {
    setImageKey(null);
    setPhotoUrl(null);
  };

  const setRow = (id: string, patch: Partial<Row>) =>
    setRows((prev) => prev.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  const addRow = (kind: Kind) =>
    setRows((prev) => [...prev, { id: uid(), shortKey: '', description: '', kind }]);
  const removeRow = (id: string) => setRows((prev) => prev.filter((r) => r.id !== id));

  const loading = sheetQuery.isLoading || tableQuery.isLoading;

  return (
    <Page name="allergen-labeling">
      <Navbar title={a.title} backLink={t.back}>
        <SettingsSaveNav isDirty={isDirty} saving={saving} onSave={save} />
      </Navbar>

      {loading ? (
        <Block className="text-align-center">
          <Preloader />
        </Block>
      ) : (
        <>
          <CapacityModeCard
            options={modeOptions}
            value={mode}
            onChange={setMode}
            heading={a.modeHeading}
            note={a.modeNote}
            infoAriaLabel={a.aboutMode}
          />

          {mode === 'SIMPLE' ? (
            <>
              <div className="allergen-photo-wrap">
                <div className="allergen-photo-tile">
                  <button
                    type="button"
                    className="allergen-photo-btn"
                    disabled={imgBusy}
                    aria-label={photoUrl ? a.replaceImage : a.uploadImage}
                    onClick={() => fileInput.current?.click()}
                    data-testid="allergen-upload-image"
                  >
                    {photoUrl ? (
                      <img src={photoUrl} alt={a.originImageTitle} />
                    ) : imgBusy ? (
                      <Preloader />
                    ) : (
                      <span className="allergen-photo-placeholder">
                        <i className="f7-icons">photo_on_rectangle</i>
                        <span>{a.uploadImage}</span>
                        <span className="allergen-photo-hint">PNG · JPG · WebP</span>
                      </span>
                    )}
                    {photoUrl && imgBusy && (
                      <span className="allergen-photo-busy">
                        <Preloader color="white" />
                      </span>
                    )}
                  </button>
                  {photoUrl && !imgBusy && (
                    <button
                      type="button"
                      className="allergen-photo-remove"
                      aria-label={a.removeImage}
                      onClick={removeImage}
                      data-testid="allergen-remove-image"
                    >
                      <Icon f7="xmark" />
                    </button>
                  )}
                </div>
                <input
                  ref={fileInput}
                  type="file"
                  accept="image/png,image/jpeg,image/webp,image/heic,image/heif"
                  hidden
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    e.target.value = '';
                    if (file) void onPickImage(file);
                  }}
                />
              </div>

              <AiSurface
                busy={parse.isPending}
                className="allergen-ai-input"
                data-testid="allergen-origin-text"
              >
                <List strongIos strongMd insetIos insetMd>
                  <ListInput
                    type="textarea"
                    resizable
                    label={a.originTextLabel}
                    placeholder={a.originTextPlaceholder}
                    value={text}
                    disabled={saving}
                    onInput={(e: React.ChangeEvent<HTMLTextAreaElement>) => setText(e.target.value)}
                  />
                </List>
              </AiSurface>
              <BlockFooter>{a.notesHint}</BlockFooter>

              <BlockTitle>{a.tableTitle}</BlockTitle>
              {cleanRows(rows).length > 0 ? (
                <>
                  {KIND_ORDER.map((k) => {
                    const items = cleanRows(rows).filter((r) => r.kind === k);
                    if (items.length === 0) return null;
                    return (
                      <div key={k}>
                        <div className="allergen-group-title">{a.kindLabel(k)}</div>
                        <div className="allergen-code-card" data-testid={`allergen-parsed-${k}`}>
                          {items.map((r) => (
                            <div
                              className="allergen-code-row allergen-code-row--readonly"
                              key={r.shortKey}
                            >
                              <span className="allergen-code-key">{r.shortKey}</span>
                              <span className="allergen-code-desc">{r.description}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                  <BlockFooter>{a.previewHint}</BlockFooter>
                </>
              ) : (
                <EmptyState
                  icon="square_list"
                  title={a.emptyTitle}
                  description={a.previewEmptyText}
                />
              )}
            </>
          ) : (
            <>
              {KIND_ORDER.map((k) => {
                const items = rows.filter((r) => r.kind === k);
                return (
                  <div key={k} data-testid={`allergen-group-${k}`}>
                    <div className="allergen-group-title">{a.kindLabel(k)}</div>
                    {items.length > 0 && (
                      <div className="allergen-code-card">
                        {items.map((row) => (
                          <div className="allergen-code-row" key={row.id}>
                            <input
                              className="allergen-code-key"
                              data-testid={`allergen-key-${row.id}`}
                              value={row.shortKey}
                              placeholder={a.keyPlaceholder}
                              onChange={(e) => setRow(row.id, { shortKey: e.target.value })}
                            />
                            <input
                              className="allergen-code-desc"
                              data-testid={`allergen-desc-${row.id}`}
                              value={row.description}
                              placeholder={a.descPlaceholder}
                              onChange={(e) => setRow(row.id, { description: e.target.value })}
                            />
                            <button
                              type="button"
                              className="allergen-code-remove"
                              aria-label={a.removeRow}
                              onClick={() => removeRow(row.id)}
                            >
                              <Icon f7="minus_circle_fill" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                    <List strongIos strongMd insetIos insetMd>
                      <ListButton data-testid={`allergen-add-${k}`} onClick={() => addRow(k)}>
                        {a.addRow}
                      </ListButton>
                    </List>
                  </div>
                );
              })}
            </>
          )}
        </>
      )}
    </Page>
  );
}
